import React from 'react';
import FormattedNumber from 'components/FormattedNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';

export interface PlanDetailHeaderCardsProps {
  estimatedAreaUnit?: string;
  totalCostTotal: number;
  fixedCostTotal: number;
  variableCostTotal: number;
  netRevenueTotal: number;
  costPerAcre: number;
  displayByFinancialAccess: boolean;
}

/**
 * The Plans detail panel's top two header cards: Total cost/Fixed cost/
 * Variable cost, and Projected net revenue/Cost per acre (FR-055,
 * 2026-08-18 design QA, via direct Figma access).
 */
const PlanDetailHeaderCards: React.FC<PlanDetailHeaderCardsProps> = ({
  estimatedAreaUnit,
  totalCostTotal,
  fixedCostTotal,
  variableCostTotal,
  netRevenueTotal,
  costPerAcre,
  displayByFinancialAccess
}) => (
  <div
    className="flex justify-between gap-4 mb-4"
    data-testid="plan-detail-header-cards"
  >
    <section className="detail-card flex-1">
      {/* `items-baseline` keeps all three stats' text aligned along a shared
          baseline, since the secondary values also carry `leading-8` to
          match the headline value's line-height. */}
      <div className="flex items-baseline py-4 px-4 pr-8">
        <div className="pr-6">
          <div className="text-sm text-neutral-60 leading-1.375rem whitespace-nowrap">
            Total cost
          </div>
          <div>
            <span className="text-2xl font-semibold text-neutral-1000 leading-8 whitespace-nowrap">
              {displayByFinancialAccess && (
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {totalCostTotal}
                </FormattedNumber>
              )}
            </span>
          </div>
        </div>

        {/* Divider only sits between Total cost and Fixed cost, not before
            Variable cost — applied explicitly rather than via a blanket
            `divide-x`. */}
        <div className="pl-6 pr-2 border-l border-neutral-20">
          <div className="text-sm text-neutral-60 leading-1.375rem">
            Fixed cost
          </div>
          <div>
            {/* Secondary stat within the "Total cost" card — smaller than
                the card's own headline value, per the reviewed Figma
                frame. */}
            <span className="text-base font-semibold text-neutral-1000 leading-8 whitespace-nowrap">
              {displayByFinancialAccess && (
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {fixedCostTotal}
                </FormattedNumber>
              )}
            </span>
          </div>
        </div>

        <div className="pl-2">
          <div className="text-sm text-neutral-60 leading-1.375rem whitespace-nowrap">
            Variable cost
          </div>
          <div>
            <span className="text-base font-semibold text-neutral-1000 leading-8 whitespace-nowrap">
              {displayByFinancialAccess && (
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {variableCostTotal}
                </FormattedNumber>
              )}
            </span>
          </div>
        </div>
      </div>
    </section>

    <section className="detail-card flex-1">
      <div className="flex items-baseline py-4 px-4 pr-8 divide-x divide-neutral-20">
        <div className="pr-6">
          <div className="text-sm text-neutral-60 leading-1.375rem whitespace-nowrap">
            Projected net revenue
          </div>
          <div>
            <span className="text-2xl font-semibold text-neutral-1000 leading-8 whitespace-nowrap">
              {displayByFinancialAccess && (
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {netRevenueTotal}
                </FormattedNumber>
              )}
            </span>
          </div>
        </div>

        <div className="pl-6">
          <div className="text-sm text-neutral-60 leading-1.375rem">
            Cost per acre
          </div>
          <div className="flex flex-row gap-0.5">
            <span className="text-base font-semibold text-neutral-1000 leading-8 whitespace-nowrap">
              {displayByFinancialAccess && Number.isFinite(costPerAcre) && (
                <FormattedNumber unit="usCurrency" roundedTo={2}>
                  {costPerAcre}
                </FormattedNumber>
              )}
            </span>
            {displayByFinancialAccess && Number.isFinite(costPerAcre) && (
              <span
                className="text-xs text-neutral-60 self-end whitespace-nowrap"
                style={{ lineHeight: '1.75rem' }}
              >
                /{getAbbreviatedUnit(estimatedAreaUnit)}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default PlanDetailHeaderCards;
